import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Mic, Plus, Search, User, Building2, Target, ChevronRight, AlertTriangle, AlertCircle, Clock, CheckCircle2, Bell, Calendar, Save, X, CalendarDays, MessageSquare, Loader2, CalendarClock, RotateCcw, Trash2, MapPin, Users, Phone, Mail, Video, Briefcase, History, Filter, RefreshCw, Send, ChevronDown, ChevronUp, HelpCircle, FileBarChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import TextNoteDialog from '../components/TextNoteDialog';
import HelpManual from '../components/HelpManual';
import RemindDialog from '../components/RemindDialog';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Activity type icons and labels
const activityTypeConfig = {
  meeting: { icon: Users, label: 'Reunión', color: 'bg-blue-100 text-blue-700' },
  call: { icon: Phone, label: 'Llamada', color: 'bg-green-100 text-green-700' },
  email: { icon: Mail, label: 'Email', color: 'bg-purple-100 text-purple-700' },
  visit: { icon: MapPin, label: 'Visita', color: 'bg-orange-100 text-orange-700' },
  reflexion: { icon: MessageSquare, label: 'Reflexión', color: 'bg-amber-100 text-amber-700' },
  evento: { icon: Calendar, label: 'Evento', color: 'bg-pink-100 text-pink-700' },
  video: { icon: Video, label: 'Videollamada', color: 'bg-indigo-100 text-indigo-700' },
  other: { icon: FileText, label: 'Otro', color: 'bg-gray-100 text-gray-700' }
};

// Client type badges
const clientTypeBadge = {
  'typsa': { label: 'TYPSA', color: 'bg-red-600 text-white' },
  'privada': { label: 'Privada', color: 'bg-emerald-600 text-white' },
  'publica': { label: 'Pública', color: 'bg-amber-600 text-white' }
};

const MySpace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isEditor, isAdmin, editorUsers } = useUser();
  const [textNotes, setTextNotes] = useState([]);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [allTextNotes, setAllTextNotes] = useState([]);
  const [allVoiceNotes, setAllVoiceNotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [notifications, setNotifications] = useState(null);
  const [notificationsFailed, setNotificationsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchVoice, setSearchVoice] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('text');
  const [showAllNotes, setShowAllNotes] = useState(false);
  
  // Set mainTab based on URL parameter - default to notifications
  const tabFromUrl = searchParams.get('tab');
  const [mainTab, setMainTab] = useState(tabFromUrl === 'notes' ? 'notes' : 'notifications');

  // Sync mainTab with URL parameter changes (e.g., when navigating between ?tab=notes and ?tab=notifications)
  useEffect(() => {
    const validTabs = ['notifications', 'notes', 'logs', 'reports', 'help'];
    if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== mainTab) {
      setMainTab(tabFromUrl);
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Filter for notifications section
  const [notificationFilter, setNotificationFilter] = useState('all'); // 'all', 'overdue_task', 'undocumented_activity', 'pending_voice_note', 'pending_followup_result'
  
  // Reminder-by-email selection (admin only feature)
  const [selectedForReminder, setSelectedForReminder] = useState(new Set()); // keys "task-<id>" or "activity-<id>"
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [remindItems, setRemindItems] = useState([]);
  
  // Collapsed groups for notes section (by default all collapsed except first)
  const [expandedNoteGroups, setExpandedNoteGroups] = useState(new Set(['0'])); // Only first group expanded by default
  const [expandedTextNotes, setExpandedTextNotes] = useState(true); // Text notes expanded by default
  
  // Modal states for actionable notifications
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityMethodModalOpen, setActivityMethodModalOpen] = useState(false); // Voice or manual selection
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Task action form state
  const [taskAction, setTaskAction] = useState('complete'); // 'complete' or 'reschedule'
  const [taskCompletionNotes, setTaskCompletionNotes] = useState('');
  const [taskNewDueDate, setTaskNewDueDate] = useState('');
  
  // Activity documentation form state (all activity fields)
  const [activityFormData, setActivityFormData] = useState({
    summary: '',
    meeting_notes: '',
    key_points: [],
    decisions: [],
    next_steps: [],
    action_items: []
  });
  
  // Audit logs state (admin only)
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsPage, setAuditLogsPage] = useState(0);
  const [auditLogFilters, setAuditLogFilters] = useState({
    action: '',
    entity_type: '',
    user_email: '',
    creator: '',
    date_from: '',
    date_to: ''
  });
  
  // Usage stats with different periods
  const [usageStatsPeriod, setUsageStatsPeriod] = useState('total');
  const [actionStatsPeriod, setActionStatsPeriod] = useState('total');
  const [userUsageStats, setUserUsageStats] = useState([]);
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [actionStats, setActionStats] = useState([]);
  const [actionStatsLoading, setActionStatsLoading] = useState(false);
  const [uniqueCreators, setUniqueCreators] = useState([]);

  useEffect(() => {
    if (user?.email) {
      fetchData();
    }
  }, [user, isAdmin]);
  
  // Auto-refresh notifications while there are voice notes pending processing
  // (transcription + classification typically finish within ~30 s).
  // Also refresh when the browser tab regains focus, so returning users see
  // fresh state without a hard reload.
  useEffect(() => {
    if (!user?.email) return undefined;

    const pendingVoiceCount = notifications?.pending_voice_notes || 0;
    // Poll every 8 s while there is at least one pending voice note.
    let pollId = null;
    if (pendingVoiceCount > 0) {
      pollId = setInterval(() => {
        fetchData({ silent: true, only: ['notifications', 'voice'] });
      }, 8000);
    }

    // Refresh on tab focus (only if we're on the notifications tab).
    const onFocus = () => {
      if (document.visibilityState === 'visible' && mainTab === 'notifications') {
        fetchData({ silent: true, only: ['notifications', 'voice'] });
      }
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      if (pollId) clearInterval(pollId);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.email, notifications?.pending_voice_notes, mainTab]);

  // Fetch audit logs when admin switches to logs tab
  useEffect(() => {
    if (isAdmin && mainTab === 'logs') {
      fetchAuditLogs();
    }
  }, [isAdmin, mainTab, auditLogsPage, auditLogFilters]);

  const fetchData = async (options = {}) => {
    const { silent = false, only = null, retryCount = 0 } = options;
    
    // Don't show loading state on silent refreshes (preserves existing data)
    if (!silent) {
      // Only set loading on initial load, not on refreshes
      if (textNotes.length === 0 && voiceNotes.length === 0) {
        setLoading(true);
      }
    }
    
    try {
      // If 'only' specified, fetch only those endpoints
      if (only) {
        const endpointMap = {
          'voice': async () => {
            const res = await axios.get(`${API}/my-notes/voice`, { timeout: 15000 });
            setVoiceNotes(res.data);
          },
          'text': async () => {
            const res = await axios.get(`${API}/my-notes/text`, { timeout: 15000 });
            setTextNotes(res.data);
          },
          'notifications': async () => {
            try {
              const res = await axios.get(`${API}/my-notes/notifications`, { timeout: 25000 });
              setNotifications(res.data);
              setNotificationsFailed(false);
            } catch (err) {
              setNotificationsFailed(true);
              throw err;
            }
          },
          'companies': async () => {
            const res = await axios.get(`${API}/companies`, { timeout: 15000 });
            setCompanies(res.data);
          },
          'opportunities': async () => {
            const res = await axios.get(`${API}/opportunities`, { timeout: 15000 });
            setOpportunities(res.data);
          }
        };
        
        const fetchPromises = only.map(key => endpointMap[key]?.()).filter(Boolean);
        await Promise.all(fetchPromises);
        return;
      }
      
      // Full fetch - using allSettled so a single slow endpoint (e.g. /companies
      // or /opportunities on production with thousands of rows) does NOT wipe
      // out notifications, voice notes and text notes. Each result is applied
      // independently. Notifications gets a longer timeout because it aggregates
      // multiple collections.
      const requestSpecs = [
        { key: 'text',          url: `${API}/my-notes/text`,          timeout: 15000 },
        { key: 'voice',         url: `${API}/my-notes/voice`,         timeout: 15000 },
        { key: 'companies',     url: `${API}/companies`,              timeout: 20000 },
        { key: 'opportunities', url: `${API}/opportunities`,          timeout: 20000 },
        { key: 'notifications', url: `${API}/my-notes/notifications`, timeout: 25000 },
        { key: 'contacts',      url: `${API}/contacts`,               timeout: 20000 },
        { key: 'typsa',         url: `${API}/typsa-org-units`,        timeout: 15000 },
      ];
      if (isAdmin) {
        requestSpecs.push({ key: 'allText',  url: `${API}/meeting-notes`, timeout: 20000 });
        requestSpecs.push({ key: 'allVoice', url: `${API}/voice-notes`,   timeout: 20000 });
      }

      const results = await Promise.allSettled(
        requestSpecs.map((s) => axios.get(s.url, { timeout: s.timeout }))
      );

      // Apply each result independently. Log which ones failed so ops can see it.
      const failed = [];
      let notificationsSucceeded = false;
      results.forEach((res, i) => {
        const key = requestSpecs[i].key;
        if (res.status === 'fulfilled') {
          const data = res.value.data;
          if (key === 'text')          setTextNotes(data);
          if (key === 'voice')         setVoiceNotes(data);
          if (key === 'companies')     setCompanies(data);
          if (key === 'opportunities') setOpportunities(data);
          if (key === 'notifications') { setNotifications(data); notificationsSucceeded = true; }
          if (key === 'contacts')      setContacts(data);
          if (key === 'typsa')         setTypsaOrgUnits(data);
          if (key === 'allText')       setAllTextNotes(data);
          if (key === 'allVoice')      setAllVoiceNotes(data);
        } else {
          failed.push({ key, reason: res.reason?.message || 'error' });
        }
      });
      // Only flag notifications as failed if it specifically didn't resolve.
      // This lets the UI distinguish "todo al día" from "no pude cargarlas".
      setNotificationsFailed(!notificationsSucceeded);
      if (failed.length > 0) {
        // Non-fatal: notifications may still be visible even if companies failed.
        console.warn('MySpace: partial load, failed endpoints:', failed);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Retry once on failure (for transient network issues)
      if (retryCount < 1 && !silent) {
        console.log('Retrying fetch...');
        setTimeout(() => fetchData({ ...options, retryCount: retryCount + 1 }), 1000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAuditLogs = async () => {
    if (!isAdmin) return;
    
    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', auditLogsPage * 50);
      params.append('limit', 50);
      
      if (auditLogFilters.action) params.append('action', auditLogFilters.action);
      if (auditLogFilters.entity_type) params.append('entity_type', auditLogFilters.entity_type);
      if (auditLogFilters.user_email) params.append('user_email', auditLogFilters.user_email);
      if (auditLogFilters.creator) params.append('creator', auditLogFilters.creator);
      if (auditLogFilters.date_from) params.append('date_from', auditLogFilters.date_from);
      if (auditLogFilters.date_to) params.append('date_to', auditLogFilters.date_to);
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setAuditLogs(response.data.items);
      setAuditLogsTotal(response.data.total);
      
      // Extract unique creators for filter
      if (!uniqueCreators.length) {
        const creators = [...new Set(response.data.items?.map(l => l.user_name).filter(Boolean) || [])];
        setUniqueCreators(prev => [...new Set([...prev, ...creators])]);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Error al cargar los logs');
    } finally {
      setAuditLogsLoading(false);
    }
  };
  
  const sendSummaryEmail = async () => {
    try {
      const response = await axios.post(`${API}/audit-logs/send-summary`);
      if (response.data.success) {
        toast.success('Email de resumen diario enviado correctamente');
      } else {
        toast.error('Error al enviar el email');
      }
    } catch (error) {
      console.error('Error sending summary email:', error);
      toast.error('Error al enviar el email de resumen');
    }
  };

  const sendWeeklyTeamSummary = async () => {
    try {
      const response = await axios.post(`${API}/audit-logs/send-weekly-team-summary`);
      if (response.data.success) {
        toast.success('Resumen semanal del equipo enviado correctamente');
      } else {
        toast.error('Error al enviar el resumen semanal');
      }
    } catch (error) {
      console.error('Error sending weekly team summary:', error);
      toast.error('Error al enviar el resumen semanal');
    }
  };

  const sendWeeklyOpportunitiesSummary = async () => {
    try {
      const response = await axios.post(`${API}/audit-logs/send-weekly-opportunities-summary`);
      if (response.data.success) {
        toast.success('Resumen de oportunidades enviado correctamente');
      } else {
        toast.error('Error al enviar el resumen de oportunidades');
      }
    } catch (error) {
      console.error('Error sending weekly opportunities summary:', error);
      toast.error('Error al enviar el resumen de oportunidades');
    }
  };
  
  const fetchUserUsageStats = async (period = 'total') => {
    if (!isAdmin) return;
    
    setUsageStatsLoading(true);
    try {
      const days = period === '24h' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 0;
      const response = await axios.get(`${API}/audit-logs/user-usage?days=${days}`);
      setUserUsageStats(response.data.users || []);
    } catch (error) {
      console.error('Error fetching user usage stats:', error);
    } finally {
      setUsageStatsLoading(false);
    }
  };
  
  const fetchActionStats = async (period = 'total') => {
    if (!isAdmin) return;
    
    setActionStatsLoading(true);
    try {
      const days = period === '24h' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 0;
      const response = await axios.get(`${API}/audit-logs/action-stats?days=${days}`);
      setActionStats(response.data.stats || []);
      // Extract unique creators for filter dropdown
      const creators = [...new Set(response.data.stats?.map(s => s.user_name) || [])];
      setUniqueCreators(creators);
    } catch (error) {
      console.error('Error fetching action stats:', error);
    } finally {
      setActionStatsLoading(false);
    }
  };
  
  // Load stats when logs tab is opened or period changes
  useEffect(() => {
    if (isAdmin && mainTab === 'logs') {
      fetchUserUsageStats(usageStatsPeriod);
    }
  }, [isAdmin, mainTab, usageStatsPeriod]);
  
  useEffect(() => {
    if (isAdmin && mainTab === 'logs') {
      fetchActionStats(actionStatsPeriod);
    }
  }, [isAdmin, mainTab, actionStatsPeriod]);

  const getCompanyName = (companyId) => {
    if (!companyId) return null;
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : null;
  };

  const getOpportunityName = (opportunityId) => {
    if (!opportunityId) return null;
    const opp = opportunities.find(o => o.id === opportunityId);
    return opp ? opp.name : null;
  };

  const getTypsaUnitName = (unitId) => {
    if (!unitId) return null;
    const unit = typsaOrgUnits.find(u => u.id === unitId);
    return unit ? unit.name : null;
  };

  const getContactName = (contactId) => {
    if (!contactId) return null;
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return null;
    return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email;
  };

  // Get notes to display based on toggle
  const displayTextNotes = showAllNotes ? allTextNotes : textNotes;
  const displayVoiceNotes = showAllNotes ? allVoiceNotes : voiceNotes;

  // Filter text notes
  const filteredTextNotes = displayTextNotes.filter(note => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      note.title?.toLowerCase().includes(searchLower) ||
      note.summary?.toLowerCase().includes(searchLower) ||
      getCompanyName(note.company_id)?.toLowerCase().includes(searchLower)
    );
  });

  // Filter voice notes
  const filteredVoiceNotes = displayVoiceNotes.filter(note => {
    if (!searchVoice) return true;
    const searchLower = searchVoice.toLowerCase();
    return (
      note.transcript_text?.toLowerCase().includes(searchLower) ||
      getCompanyName(note.company_id)?.toLowerCase().includes(searchLower)
    );
  });

  // Group voice notes by week
  const groupedVoiceNotes = useMemo(() => {
    const groups = {};
    
    filteredVoiceNotes.forEach(note => {
      const noteDate = new Date(note.date_time);
      // Get start of week (Monday)
      const startOfWeek = new Date(noteDate);
      const day = startOfWeek.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Adjust for Monday start
      startOfWeek.setDate(startOfWeek.getDate() + diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      const weekKey = startOfWeek.toISOString().split('T')[0];
      
      if (!groups[weekKey]) {
        groups[weekKey] = {
          startDate: startOfWeek,
          notes: []
        };
      }
      groups[weekKey].notes.push(note);
    });
    
    // Sort groups by date (most recent first) and sort notes within each group
    return Object.entries(groups)
      .sort(([a], [b]) => new Date(b) - new Date(a))
      .map(([key, group]) => ({
        ...group,
        weekKey: key,
        notes: group.notes.sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
      }));
  }, [filteredVoiceNotes]);

  // Get week label
  const getWeekLabel = (startDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startOfCurrentWeek = new Date(today);
    const day = startOfCurrentWeek.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    startOfCurrentWeek.setDate(startOfCurrentWeek.getDate() + diff);
    
    const startOfLastWeek = new Date(startOfCurrentWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    
    const weekStart = new Date(startDate);
    weekStart.setHours(0, 0, 0, 0);
    
    if (weekStart.getTime() === startOfCurrentWeek.getTime()) {
      return 'Esta semana';
    } else if (weekStart.getTime() === startOfLastWeek.getTime()) {
      return 'Semana pasada';
    } else {
      const endOfWeek = new Date(weekStart);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      const formatDate = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      return `${formatDate(weekStart)} - ${formatDate(endOfWeek)}`;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      meeting: 'bg-blue-100 text-blue-800',
      call: 'bg-green-100 text-green-800',
      reflexion: 'bg-amber-100 text-amber-800',
      evento: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      meeting: 'Reunion',
      call: 'Llamada',
      reflexion: 'Reflexion',
      evento: 'Evento'
    };
    return labels[type] || type;
  };

  // ─── Reminder-by-email helpers (admin only) ───
  const toggleReminderSelection = (key) => {
    setSelectedForReminder(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const buildReminderItemFromNotif = (notif) => {
    const type = notif.type === 'overdue_task' ? 'task' : 'activity';
    const ownerName = notif.owner || 'Sin responsable';
    // Best-effort resolve owner_email from the loaded users list (whitelist).
    // Backend does the same lookup + assignee_email fallback authoritative-side.
    const match = (editorUsers || []).find(u => (u.name || '').trim().toLowerCase() === ownerName.trim().toLowerCase());
    return {
      type,
      id: notif.id,
      title: notif.title,
      owner_name: ownerName,
      owner_email: match?.email || '',
    };
  };

  const openReminderForSingle = (notif) => {
    setRemindItems([buildReminderItemFromNotif(notif)]);
    setRemindDialogOpen(true);
  };

  const openReminderForSelected = () => {
    if (!notifications?.notifications) return;
    const byKey = new Map();
    for (const n of notifications.notifications) {
      const t = n.type === 'overdue_task' ? 'task' : n.type === 'undocumented_activity' ? 'activity' : null;
      if (!t) continue;
      byKey.set(`${t}-${n.id}`, n);
    }
    const chosen = Array.from(selectedForReminder)
      .map(k => byKey.get(k))
      .filter(Boolean)
      .map(buildReminderItemFromNotif);
    if (chosen.length === 0) {
      toast.error('Selecciona al menos una tarea o actividad');
      return;
    }
    setRemindItems(chosen);
    setRemindDialogOpen(true);
  };

  const handleReminderSent = () => {
    setSelectedForReminder(new Set());
    // Refresh notifications (silently)
    fetchData({ silent: true, only: ['notifications'] });
  };

  // Handle notification click - open modal or navigate
  const handleNotificationClick = (notif) => {
    setSelectedNotification(notif);
    
    if (notif.type === 'overdue_task') {
      setTaskAction('complete');
      setTaskCompletionNotes('');
      setTaskNewDueDate('');
      setTaskModalOpen(true);
    } else if (notif.type === 'undocumented_activity') {
      // Show method selection modal (voice or manual)
      setActivityMethodModalOpen(true);
    } else {
      // For voice notes, navigate to the detail page
      navigate(notif.link);
    }
  };

  // Handle documentation method selection
  const handleDocumentWithVoice = () => {
    if (!selectedNotification) return;
    setActivityMethodModalOpen(false);
    // Navigate to voice note creation with activity context
    navigate(`/voice-notes/create?activity_id=${selectedNotification.id}&company_id=${selectedNotification.company_id || ''}`);
  };

  const handleDocumentManually = () => {
    setActivityMethodModalOpen(false);
    // Reset all activity form fields and open manual form
    setActivityFormData({
      summary: '',
      meeting_notes: '',
      key_points: [],
      decisions: [],
      next_steps: [],
      action_items: []
    });
    setActivityModalOpen(true);
  };

  // Handle delete notification item
  const handleDeleteClick = (e, notif) => {
    e.stopPropagation(); // Prevent card click
    setSelectedNotification(notif);
    setDeleteDialogOpen(true);
  };

  // Delete an item (task, activity, or voice note)
  const handleDeleteConfirm = async () => {
    if (!selectedNotification) return;
    
    setActionLoading(true);
    try {
      let endpoint = '';
      let itemName = '';
      
      if (selectedNotification.type === 'overdue_task') {
        endpoint = `${API}/tasks/${selectedNotification.id}`;
        itemName = 'Tarea';
      } else if (selectedNotification.type === 'undocumented_activity') {
        endpoint = `${API}/activities/${selectedNotification.id}`;
        itemName = 'Actividad';
      } else {
        endpoint = `${API}/voice-notes/${selectedNotification.id}`;
        itemName = 'Nota de voz';
      }
      
      await axios.delete(endpoint, { headers: { 'X-User-Email': user?.email } });
      toast.success(`${itemName} eliminada correctamente`);
      
      setDeleteDialogOpen(false);
      setSelectedNotification(null);
      // Only refresh notifications, not all data
      fetchData({ silent: true, only: ['notifications', 'voice', 'text'] });
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Error al eliminar');
    } finally {
      setActionLoading(false);
    }
  };

  // Complete or reschedule a task
  const handleTaskAction = async () => {
    if (!selectedNotification) return;
    
    setActionLoading(true);
    try {
      if (taskAction === 'complete') {
        await axios.post(
          `${API}/tasks/${selectedNotification.id}/complete`,
          { 
            completion_notes: taskCompletionNotes,
            completed_by: user?.name || user?.email 
          },
          { headers: { 'X-User-Email': user?.email } }
        );
        toast.success('Tarea completada correctamente');
      } else {
        // Reschedule - update the due_date
        if (!taskNewDueDate) {
          toast.error('Por favor, selecciona una nueva fecha');
          setActionLoading(false);
          return;
        }
        await axios.put(
          `${API}/tasks/${selectedNotification.id}`,
          { 
            due_date: taskNewDueDate,
            title: selectedNotification.title,
            status: 'pending'
          },
          { headers: { 'X-User-Email': user?.email } }
        );
        toast.success('Tarea reprogramada correctamente');
      }
      
      setTaskModalOpen(false);
      setSelectedNotification(null);
      // Only refresh notifications after task action
      fetchData({ silent: true, only: ['notifications'] });
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar la tarea');
    } finally {
      setActionLoading(false);
    }
  };

  // Document an activity
  const handleDocumentActivity = async () => {
    if (!selectedNotification) return;
    
    if (!activityFormData.summary.trim()) {
      toast.error('Por favor, añade un resumen de la actividad');
      return;
    }
    
    setActionLoading(true);
    try {
      // Build payload with all filled fields
      const payload = {
        summary: activityFormData.summary,
        meeting_notes: activityFormData.meeting_notes || undefined,
        key_points: activityFormData.key_points.filter(Boolean),
        decisions: activityFormData.decisions.filter(Boolean),
        next_steps: activityFormData.next_steps.filter(Boolean),
        action_items: activityFormData.action_items.filter(Boolean),
        documented: true
      };
      
      await axios.put(
        `${API}/activities/${selectedNotification.id}`,
        payload,
        { headers: { 'X-User-Email': user?.email } }
      );
      toast.success('Actividad documentada correctamente');
      
      setActivityModalOpen(false);
      setSelectedNotification(null);
      // Only refresh notifications after documenting
      fetchData({ silent: true, only: ['notifications'] });
    } catch (error) {
      console.error('Error documenting activity:', error);
      toast.error('Error al documentar la actividad');
    } finally {
      setActionLoading(false);
    }
  };

  // Helper to check if note has real AI processed data
  const hasRealAIData = (note) => {
    const isRelationshipHistory = note?.classified_as === 'relationship_history';
    const isAccountHistory = note?.classified_as === 'account_history';
    
    if (note?.ai_structured_data && Object.keys(note.ai_structured_data).length > 0) {
      if (isRelationshipHistory) {
        return !!note.ai_structured_data.relational_analysis || !!note.ai_structured_data.context_summary;
      }
      if (isAccountHistory) {
        return !!note.ai_structured_data.strategic_analysis || !!note.ai_structured_data.executive_summary;
      }
      return true;
    }
    
    if (isRelationshipHistory) {
      return !!note?.relationship_history_ai_result;
    }
    
    if (isAccountHistory) {
      return !!note?.account_history_ai_result;
    }
    
    return !!note?.structured_summary?.key_points?.length || 
           !!note?.extracted_tasks_preview?.length ||
           !!note?.activity_ai_result;
  };

  const getStatusBadge = (note) => {
    if (note.classified_as) {
      const classLabels = {
        activity: { label: 'Documentar Actividad', color: 'bg-blue-100 text-blue-800' },
        account_history: { label: 'Historial Cuenta', color: 'bg-purple-100 text-purple-800' },
        relationship_history: { label: 'Historial Relacion', color: 'bg-green-100 text-green-800' },
        tasks: { label: 'Generar Tareas', color: 'bg-amber-100 text-amber-800' }
      };
      const cls = classLabels[note.classified_as] || { label: note.classified_as, color: 'bg-gray-100 text-gray-800' };
      
      // Check if flow is completed based on category
      const isFlowCompleted = () => {
        if (note.classified_as === 'activity') {
          return !!note.linked_activity_id;
        }
        if (note.classified_as === 'tasks') {
          return note.tasks_status === 'created' || note.tasks_generated === true;
        }
        if (note.classified_as === 'relationship_history') {
          // For relationship_history, check if linked to contact
          return !!note.linked_contact_id;
        }
        if (note.classified_as === 'account_history') {
          // For account_history, check if linked to company
          return !!note.linked_company_id;
        }
        // For other categories, check if has AI data
        return hasRealAIData(note);
      };
      
      // Check if tasks category has pending tasks
      if (note.classified_as === 'tasks' && note.tasks_status === 'pending') {
        return (
          <div className="flex gap-1 flex-wrap">
            <Badge className={cls.color}>{cls.label}</Badge>
            <Badge variant="destructive" className="text-xs">Pendiente</Badge>
          </div>
        );
      }
      
      // Show completed badge if flow is done
      if (isFlowCompleted()) {
        return (
          <div className="flex gap-1 flex-wrap">
            <Badge className={cls.color}>{cls.label}</Badge>
            <Badge className="bg-green-100 text-green-800 text-xs">Completada</Badge>
          </div>
        );
      }
      
      // Show "Sin documentar" for activity type without linked activity
      if (note.classified_as === 'activity' && !note.linked_activity_id) {
        return (
          <div className="flex gap-1 flex-wrap">
            <Badge className={cls.color}>{cls.label}</Badge>
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Sin documentar</Badge>
          </div>
        );
      }

      // Show "Sin vincular" for relationship_history type without linked contact
      if (note.classified_as === 'relationship_history' && !note.linked_contact_id) {
        return (
          <div className="flex gap-1 flex-wrap">
            <Badge className={cls.color}>{cls.label}</Badge>
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Sin vincular</Badge>
          </div>
        );
      }

      // Show "Sin vincular" for account_history type without linked company
      if (note.classified_as === 'account_history' && !note.linked_company_id) {
        return (
          <div className="flex gap-1 flex-wrap">
            <Badge className={cls.color}>{cls.label}</Badge>
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Sin vincular</Badge>
          </div>
        );
      }
      
      return <Badge className={cls.color}>{cls.label}</Badge>;
    }
    if (!note.transcript_text) return <Badge variant="secondary">Pendiente transcripcion</Badge>;
    if (!note.structured_summary) return <Badge variant="secondary">Pendiente procesamiento</Badge>;
    return <Badge variant="default">Procesado</Badge>;
  };

  // Toggle group expansion
  const toggleNoteGroup = (groupIndex) => {
    setExpandedNoteGroups(prev => {
      const newSet = new Set(prev);
      const key = String(groupIndex);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Delete voice note
  const handleDeleteVoiceNote = async (noteId, e) => {
    e.stopPropagation(); // Prevent card click
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta nota de voz?')) return;
    
    try {
      await axios.delete(`${API}/voice-notes/${noteId}`, {
        headers: { 'x-user-email': user?.email }
      });
      toast.success('Nota de voz eliminada');
      // Only refresh voice notes
      fetchData({ silent: true, only: ['voice', 'notifications'] });
    } catch (error) {
      console.error('Error deleting voice note:', error);
      toast.error('Error al eliminar la nota de voz');
    }
  };

  // Delete text note
  const handleDeleteTextNote = async (noteId, e) => {
    e.stopPropagation(); // Prevent card click
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta nota?')) return;
    
    try {
      await axios.delete(`${API}/meeting-notes/${noteId}`, {
        headers: { 'x-user-email': user?.email }
      });
      toast.success('Nota eliminada');
      // Only refresh text notes
      fetchData({ silent: true, only: ['text', 'notifications'] });
    } catch (error) {
      console.error('Error deleting text note:', error);
      toast.error('Error al eliminar la nota');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-sm text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 overflow-x-hidden" data-testid="my-space-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="my-space-title">
            Mi Espacio
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tus notas y notificaciones
          </p>
        </div>
      </div>

      {/* Main Tabs: Notifications / Notes / Logs (admin only) / Reports (admin only) / Help */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-6">
        <TabsList className={`grid w-full max-w-xl ${isAdmin ? 'grid-cols-5' : 'grid-cols-3'}`}>
          <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Notificaciones</span>
            <span className="sm:hidden">Notif.</span>
            {notifications && notifications.total > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {notifications.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="tab-notes">
            <Mic className="w-4 h-4" />
            <span className="hidden sm:inline">Mis Notas</span>
            <span className="sm:hidden">Notas</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="logs" className="flex items-center gap-2" data-testid="tab-logs">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Registro</span>
              <span className="sm:hidden">Logs</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="reports" className="flex items-center gap-2" data-testid="tab-reports">
              <FileBarChart className="w-4 h-4" />
              <span className="hidden sm:inline">Informes</span>
              <span className="sm:hidden">Info.</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="help" className="flex items-center gap-2" data-testid="tab-help">
            <HelpCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Ayuda</span>
            <span className="sm:hidden">Ayuda</span>
          </TabsTrigger>
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          {renderNotificationsContent()}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-6">
          {/* Admin toggle to see all notes - inside notes section */}
          {isAdmin && (
            <div className="flex items-center justify-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAllNotes}
                  onChange={(e) => setShowAllNotes(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                  data-testid="toggle-all-notes"
                />
                <span className="text-sm font-medium">
                  Ver todas las notas
                </span>
                {showAllNotes && (
                  <Badge variant="secondary" className="text-xs">Admin</Badge>
                )}
              </label>
            </div>
          )}
          
          {/* Sub-tabs for text/voice notes when admin */}
          {isAdmin && showAllNotes ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full max-w-sm grid-cols-2">
                <TabsTrigger value="text" className="flex items-center gap-2" data-testid="tab-text-notes">
                  <FileText className="w-4 h-4" />
                  Texto ({displayTextNotes.length})
                </TabsTrigger>
                <TabsTrigger value="voice" className="flex items-center gap-2" data-testid="tab-voice-notes">
                  <Mic className="w-4 h-4" />
                  Voz ({displayVoiceNotes.length})
                </TabsTrigger>
              </TabsList>

              {/* Text Notes Tab - Only in Admin mode */}
              <TabsContent value="text" className="space-y-4">
                {renderTextNotesContent()}
              </TabsContent>

              {/* Voice Notes Tab */}
              <TabsContent value="voice" className="space-y-4">
                {renderVoiceNotesContent()}
              </TabsContent>
            </Tabs>
          ) : (
            /* Regular user view - Only Voice Notes */
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Mis Notas de Voz ({displayVoiceNotes.length})
              </h2>
              {renderVoiceNotesContent()}
            </div>
          )}
        </TabsContent>
        
        {/* Logs Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="logs" className="space-y-6" data-testid="logs-section">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Registro de Actividad
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {auditLogsTotal} acciones registradas
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAuditLogs()}
                    disabled={auditLogsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${auditLogsLoading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendSummaryEmail}
                    data-testid="send-daily-summary-btn"
                    title="Envía el correo de resumen diario (cubre el día anterior, o Vie+Sáb+Dom si es lunes)"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Resumen diario
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendWeeklyTeamSummary}
                    data-testid="send-weekly-team-summary-btn"
                    title="Resumen de empresas y contactos nuevos (últimos 7 días)"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Resumen semanal
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendWeeklyOpportunitiesSummary}
                    data-testid="send-weekly-opportunities-summary-btn"
                    title="Resumen de oportunidades de la semana para dirección (ganadas, nuevas, cambios de estado)"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Resumen oportunidades
                  </Button>
                </div>
              </div>
              
              {/* Rankings Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* User Usage Stats Ranking */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Ranking Tiempo de Uso
                      </CardTitle>
                      <Select value={usageStatsPeriod} onValueChange={setUsageStatsPeriod}>
                        <SelectTrigger className="h-8 w-[130px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24h">Últimas 24h</SelectItem>
                          <SelectItem value="7d">Últimos 7 días</SelectItem>
                          <SelectItem value="30d">Últimos 30 días</SelectItem>
                          <SelectItem value="total">Total</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {usageStatsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : userUsageStats.length > 0 ? (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                        {userUsageStats.map((stat, index) => (
                          <div key={stat.user_email} className="flex items-center gap-3 p-2 rounded-md bg-muted/30 hover:bg-muted/50">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-gray-200 text-gray-700' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{stat.user_name}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {stat.last_activity && `Última: ${new Date(stat.last_activity).toLocaleString('es-ES', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                })}`}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold">{stat.total_hours}h</p>
                              <p className="text-[10px] text-muted-foreground">{stat.total_minutes} min</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Sin datos de uso para este período
                      </p>
                    )}
                  </CardContent>
                </Card>
                
                {/* Action Stats Ranking */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Ranking Acciones por Usuario
                      </CardTitle>
                      <Select value={actionStatsPeriod} onValueChange={setActionStatsPeriod}>
                        <SelectTrigger className="h-8 w-[130px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24h">Últimas 24h</SelectItem>
                          <SelectItem value="7d">Últimos 7 días</SelectItem>
                          <SelectItem value="30d">Últimos 30 días</SelectItem>
                          <SelectItem value="total">Total</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {actionStatsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : actionStats.length > 0 ? (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                        {actionStats.map((stat, index) => (
                          <div key={stat.user_email} className="flex items-center gap-3 p-2 rounded-md bg-muted/30 hover:bg-muted/50">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-gray-200 text-gray-700' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{stat.user_name}</p>
                              <div className="flex gap-2 text-[10px]">
                                <span className="text-green-600">+{stat.creates} crear</span>
                                <span className="text-blue-600">{stat.updates} editar</span>
                                <span className="text-red-600">-{stat.deletes} borrar</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold">{stat.total_actions}</p>
                              <p className="text-[10px] text-muted-foreground">acciones</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Sin acciones para este período
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Acción</Label>
                  <Select
                    value={auditLogFilters.action}
                    onValueChange={(v) => {
                      setAuditLogFilters(prev => ({ ...prev, action: v === 'all' ? '' : v }));
                      setAuditLogsPage(0);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[120px] text-xs">
                      <SelectValue placeholder="Acción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="create">Crear</SelectItem>
                      <SelectItem value="update">Editar</SelectItem>
                      <SelectItem value="delete">Eliminar</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="logout">Logout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Entidad</Label>
                  <Select
                    value={auditLogFilters.entity_type}
                    onValueChange={(v) => {
                      setAuditLogFilters(prev => ({ ...prev, entity_type: v === 'all' ? '' : v }));
                      setAuditLogsPage(0);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[120px] text-xs">
                      <SelectValue placeholder="Entidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="activity">Actividad</SelectItem>
                      <SelectItem value="company">Empresa</SelectItem>
                      <SelectItem value="contact">Contacto</SelectItem>
                      <SelectItem value="opportunity">Oportunidad</SelectItem>
                      <SelectItem value="task">Tarea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Creador</Label>
                  <Select
                    value={auditLogFilters.creator || 'all'}
                    onValueChange={(v) => {
                      setAuditLogFilters(prev => ({ ...prev, creator: v === 'all' ? '' : v }));
                      setAuditLogsPage(0);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[150px] text-xs">
                      <SelectValue placeholder="Creador" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniqueCreators.map(creator => (
                        <SelectItem key={creator} value={creator}>{creator}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Desde</Label>
                  <Input
                    type="date"
                    value={auditLogFilters.date_from}
                    onChange={(e) => {
                      setAuditLogFilters(prev => ({ ...prev, date_from: e.target.value }));
                      setAuditLogsPage(0);
                    }}
                    className="h-8 w-[130px] text-xs"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Hasta</Label>
                  <Input
                    type="date"
                    value={auditLogFilters.date_to}
                    onChange={(e) => {
                      setAuditLogFilters(prev => ({ ...prev, date_to: e.target.value }));
                      setAuditLogsPage(0);
                    }}
                    className="h-8 w-[130px] text-xs"
                  />
                </div>
                
                {(auditLogFilters.action || auditLogFilters.entity_type || auditLogFilters.creator || auditLogFilters.date_from || auditLogFilters.date_to) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      setAuditLogFilters({ action: '', entity_type: '', user_email: '', creator: '', date_from: '', date_to: '' });
                      setAuditLogsPage(0);
                    }}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>
              
              {/* Logs List */}
              <div className="space-y-2">
                {auditLogsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay registros de actividad</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {auditLogs.map((log) => {
                      const actionColors = {
                        create: 'bg-green-100 text-green-700',
                        update: 'bg-blue-100 text-blue-700',
                        delete: 'bg-red-100 text-red-700',
                        login: 'bg-cyan-100 text-cyan-700',
                        logout: 'bg-gray-100 text-gray-700',
                        transcribe: 'bg-purple-100 text-purple-700',
                        process_ai: 'bg-amber-100 text-amber-700'
                      };
                      const actionLabels = {
                        create: 'Creación',
                        update: 'Actualización',
                        delete: 'Eliminación',
                        login: 'Inicio sesión',
                        logout: 'Cierre sesión',
                        transcribe: 'Transcripción',
                        process_ai: 'Proceso IA'
                      };
                      const entityLabels = {
                        company: 'Empresa',
                        contact: 'Contacto',
                        opportunity: 'Oportunidad',
                        activity: 'Actividad',
                        task: 'Tarea',
                        voice_note: 'Nota de voz',
                        text_note: 'Nota de texto',
                        meeting_note: 'Nota de reunión',
                        user: 'Usuario'
                      };
                      
                      return (
                        <Card key={log.id} className="hover:bg-muted/30 transition-colors">
                          <CardContent className="py-3 px-4">
                            <div className="flex items-start gap-3">
                              <Badge className={`${actionColors[log.action] || 'bg-gray-100 text-gray-700'} shrink-0 text-xs`}>
                                {actionLabels[log.action] || log.action}
                              </Badge>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium truncate">{log.entity_name}</span>
                                  <Badge variant="outline" className="text-xs shrink-0">
                                    {entityLabels[log.entity_type] || log.entity_type}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {log.user_name}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(log.timestamp).toLocaleString('es-ES', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                    
                    {/* Pagination */}
                    {auditLogsTotal > 50 && (
                      <div className="flex items-center justify-center gap-2 pt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAuditLogsPage(p => Math.max(0, p - 1))}
                          disabled={auditLogsPage === 0}
                        >
                          Anterior
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Página {auditLogsPage + 1} de {Math.ceil(auditLogsTotal / 50)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAuditLogsPage(p => p + 1)}
                          disabled={(auditLogsPage + 1) * 50 >= auditLogsTotal}
                        >
                          Siguiente
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        )}
        
        {/* Reports Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="reports" className="space-y-6" data-testid="reports-section">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Informes Técnicos</h2>
                  <p className="text-sm text-muted-foreground">Accede a los informes técnico-económicos del CRM</p>
                </div>
              </div>
              
              {/* Informe base/actual */}
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/technical-report')}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <FileBarChart className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">Informe Técnico-Económico Base</h3>
                      <p className="text-sm text-muted-foreground">
                        Análisis completo del desarrollo inicial del CRM: arquitectura, costes, comparativas y ROI
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">Descargable en PDF</Badge>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              
              {/* Informe mensual pendiente */}
              <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Informe Mensual Pendiente
                    </CardTitle>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </Badge>
                  </div>
                  <CardDescription>
                    Introduce los datos de este mes para poder emitir el informe
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthly-hours">Horas dedicadas este mes *</Label>
                      <Input 
                        id="monthly-hours" 
                        type="number" 
                        placeholder="0" 
                        min="0"
                        step="0.5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthly-rate">Tarifa por hora (€)</Label>
                      <Input 
                        id="monthly-rate" 
                        type="number" 
                        defaultValue="142"
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthly-credits">Créditos Emergent gastados (€) *</Label>
                      <Input 
                        id="monthly-credits" 
                        type="number" 
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <Label htmlFor="monthly-features">Funcionalidades añadidas este mes</Label>
                    <Textarea 
                      id="monthly-features" 
                      placeholder="Describe las nuevas funcionalidades implementadas..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="monthly-bugs">Bugs corregidos</Label>
                    <Textarea 
                      id="monthly-bugs" 
                      placeholder="Lista los bugs corregidos..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="monthly-notes">Notas adicionales</Label>
                    <Textarea 
                      id="monthly-notes" 
                      placeholder="Cualquier información relevante del mes..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline">
                      <Save className="w-4 h-4 mr-2" />
                      Guardar borrador
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" disabled>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Emitir informe
                    </Button>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    * Campos obligatorios para emitir el informe. El botón se habilitará cuando completes los datos requeridos.
                  </p>
                </CardContent>
              </Card>
              
              <p className="text-xs text-muted-foreground text-center">
                Los informes mensuales complementan el informe base con el trabajo realizado cada mes
              </p>
            </div>
          </TabsContent>
        )}
        
        {/* Help Tab */}
        <TabsContent value="help" className="h-[calc(100vh-280px)]" data-testid="help-section">
          <HelpManual />
        </TabsContent>
      </Tabs>

      <TextNoteDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={fetchData} />
      
      {/* Task Action Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="task-action-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Tarea Vencida
            </DialogTitle>
            <DialogDescription>
              {selectedNotification?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Task info */}
            {selectedNotification?.description && (
              <p className="text-sm text-muted-foreground">{selectedNotification.description}</p>
            )}
            
            {selectedNotification?.days_overdue > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="destructive">
                  {selectedNotification.days_overdue} día{selectedNotification.days_overdue !== 1 ? 's' : ''} de retraso
                </Badge>
                <span className="text-muted-foreground">
                  Fecha original: {selectedNotification?.due_date && new Date(selectedNotification.due_date).toLocaleDateString('es-ES')}
                </span>
              </div>
            )}
            
            <Separator />
            
            {/* Action selector */}
            <div className="space-y-3">
              <Label>¿Qué deseas hacer?</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={taskAction === 'complete' ? 'default' : 'outline'}
                  onClick={() => setTaskAction('complete')}
                  className="justify-start"
                  data-testid="task-action-complete"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completar
                </Button>
                <Button
                  variant={taskAction === 'reschedule' ? 'default' : 'outline'}
                  onClick={() => setTaskAction('reschedule')}
                  className="justify-start"
                  data-testid="task-action-reschedule"
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Reprogramar
                </Button>
              </div>
            </div>
            
            {/* Action-specific fields */}
            {taskAction === 'complete' && (
              <div className="space-y-2">
                <Label htmlFor="completion-notes">Notas de finalización (opcional)</Label>
                <Textarea
                  id="completion-notes"
                  value={taskCompletionNotes}
                  onChange={(e) => setTaskCompletionNotes(e.target.value)}
                  placeholder="Describe cómo se completó la tarea..."
                  className="resize-none"
                  data-testid="task-completion-notes"
                />
              </div>
            )}
            
            {taskAction === 'reschedule' && (
              <div className="space-y-2">
                <Label htmlFor="new-due-date">Nueva fecha de vencimiento</Label>
                <Input
                  id="new-due-date"
                  type="date"
                  value={taskNewDueDate}
                  onChange={(e) => setTaskNewDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="task-new-due-date"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setTaskModalOpen(false);
                setSelectedNotification(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleTaskAction}
              disabled={actionLoading}
              className={taskAction === 'complete' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="task-action-submit"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : taskAction === 'complete' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Marcar completada
                </>
              ) : (
                <>
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Reprogramar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Activity Documentation Modal */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="activity-doc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNotification?.activity_type && activityTypeConfig[selectedNotification.activity_type] ? (
                <>
                  {React.createElement(activityTypeConfig[selectedNotification.activity_type].icon, { className: "w-5 h-5" })}
                </>
              ) : (
                <Clock className="w-5 h-5 text-amber-500" />
              )}
              Documentar Actividad
            </DialogTitle>
            <DialogDescription>
              {selectedNotification?.title}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Activity info card */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedNotification?.description && (
                    <Badge className={activityTypeConfig[selectedNotification.activity_type]?.color || 'bg-gray-100'}>
                      {selectedNotification.description}
                    </Badge>
                  )}
                  {selectedNotification?.due_date && (
                    <span className="text-sm text-muted-foreground">
                      {new Date(selectedNotification.due_date).toLocaleDateString('es-ES')}
                      {selectedNotification?.time && ` a las ${selectedNotification.time}`}
                    </span>
                  )}
                  {selectedNotification?.days_overdue > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {selectedNotification.days_overdue} día{selectedNotification.days_overdue !== 1 ? 's' : ''} de retraso
                    </Badge>
                  )}
                </div>
                
                {/* Additional info */}
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {(selectedNotification?.company_id || selectedNotification?.typsa_org_unit_id) && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {getCompanyName(selectedNotification.company_id) || getTypsaUnitName(selectedNotification.typsa_org_unit_id)}
                    </span>
                  )}
                  {selectedNotification?.opportunity_id && (
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {getOpportunityName(selectedNotification.opportunity_id)}
                    </span>
                  )}
                  {selectedNotification?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedNotification.location}
                    </span>
                  )}
                  {selectedNotification?.contact_ids?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {selectedNotification.contact_ids.map(id => getContactName(id)).filter(Boolean).join(', ')}
                    </span>
                  )}
                </div>
              </div>
              
              <Separator />
              
              {/* Documentation fields */}
              <div className="space-y-4">
                {/* Summary - Required */}
                <div className="space-y-2">
                  <Label htmlFor="activity-summary" className="text-sm font-semibold">
                    Resumen de la actividad *
                  </Label>
                  <Textarea
                    id="activity-summary"
                    value={activityFormData.summary}
                    onChange={(e) => setActivityFormData(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Resume los puntos principales de la actividad..."
                    className="resize-none min-h-[100px]"
                    data-testid="activity-summary-input"
                  />
                </div>
                
                {/* Meeting Notes */}
                <div className="space-y-2">
                  <Label htmlFor="meeting-notes" className="text-sm font-semibold">
                    Notas de la reunión
                  </Label>
                  <Textarea
                    id="meeting-notes"
                    value={activityFormData.meeting_notes}
                    onChange={(e) => setActivityFormData(prev => ({ ...prev, meeting_notes: e.target.value }))}
                    placeholder="Notas detalladas de lo que se habló..."
                    className="resize-none min-h-[80px]"
                    data-testid="activity-meeting-notes"
                  />
                </div>
                
                {/* Key Points */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Puntos clave</Label>
                  <div className="space-y-2">
                    {activityFormData.key_points.map((point, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={point}
                          onChange={(e) => {
                            const newPoints = [...activityFormData.key_points];
                            newPoints[idx] = e.target.value;
                            setActivityFormData(prev => ({ ...prev, key_points: newPoints }));
                          }}
                          placeholder="Punto clave..."
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newPoints = activityFormData.key_points.filter((_, i) => i !== idx);
                            setActivityFormData(prev => ({ ...prev, key_points: newPoints }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivityFormData(prev => ({ ...prev, key_points: [...prev.key_points, ''] }))}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Añadir punto
                    </Button>
                  </div>
                </div>
                
                {/* Decisions */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Decisiones tomadas</Label>
                  <div className="space-y-2">
                    {activityFormData.decisions.map((decision, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={decision}
                          onChange={(e) => {
                            const newDecisions = [...activityFormData.decisions];
                            newDecisions[idx] = e.target.value;
                            setActivityFormData(prev => ({ ...prev, decisions: newDecisions }));
                          }}
                          placeholder="Decisión tomada..."
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newDecisions = activityFormData.decisions.filter((_, i) => i !== idx);
                            setActivityFormData(prev => ({ ...prev, decisions: newDecisions }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivityFormData(prev => ({ ...prev, decisions: [...prev.decisions, ''] }))}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Añadir decisión
                    </Button>
                  </div>
                </div>
                
                {/* Next Steps */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Próximos pasos</Label>
                  <div className="space-y-2">
                    {activityFormData.next_steps.map((step, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={step}
                          onChange={(e) => {
                            const newSteps = [...activityFormData.next_steps];
                            newSteps[idx] = e.target.value;
                            setActivityFormData(prev => ({ ...prev, next_steps: newSteps }));
                          }}
                          placeholder="Próximo paso..."
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newSteps = activityFormData.next_steps.filter((_, i) => i !== idx);
                            setActivityFormData(prev => ({ ...prev, next_steps: newSteps }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivityFormData(prev => ({ ...prev, next_steps: [...prev.next_steps, ''] }))}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Añadir paso
                    </Button>
                  </div>
                </div>
                
                {/* Action Items */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Acciones pendientes</Label>
                  <div className="space-y-2">
                    {activityFormData.action_items.map((item, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          value={item}
                          onChange={(e) => {
                            const newItems = [...activityFormData.action_items];
                            newItems[idx] = e.target.value;
                            setActivityFormData(prev => ({ ...prev, action_items: newItems }));
                          }}
                          placeholder="Acción pendiente..."
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newItems = activityFormData.action_items.filter((_, i) => i !== idx);
                            setActivityFormData(prev => ({ ...prev, action_items: newItems }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActivityFormData(prev => ({ ...prev, action_items: [...prev.action_items, ''] }))}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Añadir acción
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setActivityModalOpen(false);
                setSelectedNotification(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setActivityModalOpen(false);
                navigate(`/activities`);
              }}
              data-testid="activity-view-detail"
            >
              Ver en Actividades
            </Button>
            <Button
              onClick={handleDocumentActivity}
              disabled={actionLoading || !activityFormData.summary.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="activity-doc-submit"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Documentar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar "{selectedNotification?.title}"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setSelectedNotification(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={actionLoading}
              data-testid="confirm-delete"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Activity Documentation Method Selection Dialog */}
      <Dialog open={activityMethodModalOpen} onOpenChange={setActivityMethodModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="activity-method-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Documentar Actividad
            </DialogTitle>
            <DialogDescription>
              {selectedNotification?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              ¿Cómo deseas documentar esta actividad?
            </p>
            
            {/* Activity info */}
            {selectedNotification && (
              <div className="bg-muted/50 rounded-lg p-3 mb-4 space-y-1 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={activityTypeConfig[selectedNotification.activity_type]?.color || 'bg-gray-100'}>
                    {selectedNotification.description}
                  </Badge>
                  {selectedNotification.client_type && clientTypeBadge[selectedNotification.client_type] && (
                    <Badge className={clientTypeBadge[selectedNotification.client_type].color}>
                      {clientTypeBadge[selectedNotification.client_type].label}
                    </Badge>
                  )}
                </div>
                {selectedNotification.company_name && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Building2 className="w-3 h-3" />
                    <span>{selectedNotification.company_name}</span>
                  </div>
                )}
                {selectedNotification.typsa_unit_name && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Briefcase className="w-3 h-3" />
                    <span>{selectedNotification.typsa_unit_name}</span>
                    {selectedNotification.typsa_unit_type && (
                      <span className="text-xs">({selectedNotification.typsa_unit_type})</span>
                    )}
                  </div>
                )}
                {selectedNotification.contact_names?.length > 0 && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Users className="w-3 h-3" />
                    <span>{selectedNotification.contact_names.join(', ')}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="grid gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 justify-start"
                onClick={handleDocumentWithVoice}
                data-testid="document-with-voice"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                    <Mic className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">Documentar con voz</div>
                    <div className="text-xs text-muted-foreground">
                      Graba una nota de voz y la IA extraerá los puntos clave
                    </div>
                  </div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto py-4 justify-start"
                onClick={handleDocumentManually}
                data-testid="document-manually"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-100 text-amber-600">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">Documentar manualmente</div>
                    <div className="text-xs text-muted-foreground">
                      Completa los campos de la actividad de forma manual
                    </div>
                  </div>
                </div>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setActivityMethodModalOpen(false);
                setSelectedNotification(null);
              }}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder-by-email dialog (shared) */}
      <RemindDialog
        open={remindDialogOpen}
        onOpenChange={setRemindDialogOpen}
        items={remindItems}
        onSent={handleReminderSent}
      />
    </div>
  );

  // Helper function to render notifications content
  function renderNotificationsContent() {
    // Clear error state when the notifications endpoint failed. Do NOT pretend
    // the user is "al día" — that was the recurring bug in Feb 2026.
    if (notificationsFailed && !notifications) {
      return (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="notifications-load-error">
          <CardContent className="flex flex-col items-center justify-center py-10">
            <AlertCircle className="w-10 h-10 text-amber-500 mb-3" />
            <p className="text-base font-medium text-center">No he podido cargar tus notificaciones</p>
            <p className="text-sm text-muted-foreground text-center mt-1">
              El servidor tardó demasiado en responder. Tus datos siguen ahí, solo hay que reintentar.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => fetchData({ only: ['notifications'] })}
              data-testid="notifications-retry-btn"
            >
              Reintentar
            </Button>
          </CardContent>
        </Card>
      );
    }
    if (!notifications || notifications.total === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
            <p className="text-lg font-medium text-center">¡Todo al día!</p>
            <p className="text-sm text-muted-foreground text-center mt-2">
              No tienes notificaciones pendientes
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4 overflow-hidden" data-testid="notifications-section">
        {/* Summary cards - clickable for filtering */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          <div 
            className={`rounded-lg p-2 sm:p-3 text-center cursor-pointer transition-all hover:shadow-md ${
              notificationFilter === 'overdue_task' 
                ? 'bg-red-200 border-2 border-red-500 ring-2 ring-red-300' 
                : 'bg-red-50 border border-red-200 hover:bg-red-100'
            }`}
            onClick={() => setNotificationFilter(notificationFilter === 'overdue_task' ? 'all' : 'overdue_task')}
            data-testid="filter-overdue-tasks"
          >
            <div className="text-xl sm:text-2xl font-bold text-red-600">{notifications.overdue_tasks}</div>
            <div className="text-[10px] sm:text-xs text-red-700">Tareas vencidas</div>
          </div>
          <div 
            className={`rounded-lg p-2 sm:p-3 text-center cursor-pointer transition-all hover:shadow-md ${
              notificationFilter === 'undocumented_activity' 
                ? 'bg-amber-200 border-2 border-amber-500 ring-2 ring-amber-300' 
                : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'
            }`}
            onClick={() => setNotificationFilter(notificationFilter === 'undocumented_activity' ? 'all' : 'undocumented_activity')}
            data-testid="filter-undocumented"
          >
            <div className="text-xl sm:text-2xl font-bold text-amber-600">{notifications.undocumented_activities}</div>
            <div className="text-[10px] sm:text-xs text-amber-700">Sin documentar</div>
          </div>
          <div 
            className={`rounded-lg p-2 sm:p-3 text-center cursor-pointer transition-all hover:shadow-md ${
              notificationFilter === 'pending_voice_note' 
                ? 'bg-blue-200 border-2 border-blue-500 ring-2 ring-blue-300' 
                : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
            }`}
            onClick={() => setNotificationFilter(notificationFilter === 'pending_voice_note' ? 'all' : 'pending_voice_note')}
            data-testid="filter-pending-voice"
          >
            <div className="text-xl sm:text-2xl font-bold text-blue-600">{notifications.pending_voice_notes}</div>
            <div className="text-[10px] sm:text-xs text-blue-700">Notas pend.</div>
          </div>
          <div
            className={`rounded-lg p-2 sm:p-3 text-center cursor-pointer transition-all hover:shadow-md ${
              notificationFilter === 'pending_followup_result'
                ? 'bg-orange-200 border-2 border-orange-500 ring-2 ring-orange-300'
                : 'bg-orange-50 border border-orange-200 hover:bg-orange-100'
            }`}
            onClick={() => setNotificationFilter(notificationFilter === 'pending_followup_result' ? 'all' : 'pending_followup_result')}
            data-testid="filter-pending-followup"
          >
            <div className="text-xl sm:text-2xl font-bold text-orange-600">{notifications.pending_followup_results ?? 0}</div>
            <div className="text-[10px] sm:text-xs text-orange-700">Adjudic. a seguir</div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-orange-500" />
          <h2 className="text-lg font-semibold">
            {notificationFilter === 'all' ? 'Pendientes' 
              : notificationFilter === 'overdue_task' ? 'Tareas Vencidas'
              : notificationFilter === 'undocumented_activity' ? 'Sin Documentar'
              : notificationFilter === 'pending_followup_result' ? 'Adjudicaciones a Seguir'
              : 'Notas de Voz Pendientes'}
          </h2>
          <Badge variant="destructive">
            {notificationFilter === 'all' 
              ? notifications.total 
              : notifications.notifications.filter(n => n.type === notificationFilter).length}
          </Badge>
          {notificationFilter !== 'all' && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs"
              onClick={() => setNotificationFilter('all')}
            >
              <X className="w-3 h-3 mr-1" />
              Limpiar
            </Button>
          )}
          {notificationFilter === 'undocumented_activity' && (
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 px-2 text-xs ml-auto"
              onClick={() => navigate('/activities?owner=me')}
              data-testid="view-in-activities-btn"
            >
              <ChevronRight className="w-3 h-3 mr-1" />
              Ver en Actividades
            </Button>
          )}
          {isAdmin && selectedForReminder.size > 0 && (
            <Button
              variant="default"
              size="sm"
              className="h-7 px-3 text-xs ml-auto"
              onClick={() => openReminderForSelected()}
              data-testid="remind-selected-btn"
            >
              <Send className="w-3 h-3 mr-1" />
              Recordar seleccionados ({selectedForReminder.size})
            </Button>
          )}
        </div>
        
        <div className="grid gap-3">
          {notifications.notifications
            .filter(notif => notificationFilter === 'all' || notif.type === notificationFilter)
            .map((notif) => {
            const typeConfig = activityTypeConfig[notif.activity_type] || activityTypeConfig.other;
            const TypeIcon = typeConfig.icon;
            
            const remindable = (notif.type === 'overdue_task' || notif.type === 'undocumented_activity');
            const remindKey = `${notif.type === 'overdue_task' ? 'task' : 'activity'}-${notif.id}`;
            const isSelected = selectedForReminder.has(remindKey);
            return (
              <Card 
                key={`${notif.type}-${notif.id}`}
                className={`hover:shadow-md transition-shadow border-l-4 ${
                  notif.type === 'overdue_task' 
                    ? 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20' 
                    : notif.type === 'undocumented_activity'
                    ? 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20'
                    : notif.type === 'pending_followup_result'
                    ? 'border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20'
                    : 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                }`}
                data-testid={`notification-${notif.type}-${notif.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    {/* Selection checkbox (admin + task/activity only) */}
                    {isAdmin && remindable && (
                      <div className="flex items-center pt-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleReminderSelection(remindKey)}
                          data-testid={`remind-checkbox-${notif.type}-${notif.id}`}
                          aria-label="Seleccionar para recordatorio"
                        />
                      </div>
                    )}
                    {/* Icon */}
                    <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                      notif.type === 'overdue_task' 
                        ? 'bg-red-100 text-red-600' 
                        : notif.type === 'undocumented_activity'
                        ? typeConfig.color
                        : notif.type === 'pending_followup_result'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {notif.type === 'overdue_task' ? (
                        <AlertTriangle className="w-5 h-5" />
                      ) : notif.type === 'undocumented_activity' ? (
                        <TypeIcon className="w-5 h-5" />
                      ) : notif.type === 'pending_followup_result' ? (
                        <Bell className="w-5 h-5" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0 cursor-pointer overflow-hidden" onClick={() => handleNotificationClick(notif)}>
                      {/* Header */}
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-sm truncate max-w-[200px] sm:max-w-none">{notif.title}</span>
                        <Badge 
                          variant={notif.type === 'overdue_task' ? 'destructive' : 'secondary'}
                          className="text-xs shrink-0"
                        >
                          {notif.type === 'overdue_task' 
                            ? 'Tarea vencida' 
                            : notif.type === 'undocumented_activity'
                            ? notif.description
                            : notif.description
                          }
                        </Badge>
                        {/* Client type badge */}
                        {notif.client_type && clientTypeBadge[notif.client_type] && (
                          <Badge className={`text-xs shrink-0 ${clientTypeBadge[notif.client_type].color}`}>
                            {clientTypeBadge[notif.client_type].label}
                          </Badge>
                        )}
                        {notif.priority_label && notif.priority !== 'medium' && (
                          <Badge variant="outline" className={`text-xs shrink-0 ${
                            notif.priority === 'high' ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'
                          }`}>
                            Prioridad {notif.priority_label}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Details */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-2">
                        {/* Date */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Calendar className="w-3 h-3" />
                          <span>{notif.due_date ? new Date(notif.due_date).toLocaleDateString('es-ES') : 'Sin fecha'}</span>
                          {notif.time && <span>• {notif.time}</span>}
                        </div>
                        
                        {/* Days overdue */}
                        {notif.days_overdue > 0 && (
                          <span className="text-red-600 font-medium shrink-0">
                            {notif.days_overdue}d retraso
                          </span>
                        )}
                        
                        {/* Company name (from backend enrichment) */}
                        {notif.company_name && (
                          <div className="flex items-center gap-1 min-w-0">
                            <Building2 className="w-3 h-3 shrink-0" />
                            <span className="font-medium truncate">{notif.company_name}</span>
                          </div>
                        )}
                        
                        {/* TYPSA Unit with type */}
                        {notif.typsa_unit_name && (
                          <div className="flex items-center gap-1 min-w-0">
                            <Briefcase className="w-3 h-3 shrink-0" />
                            <span className="font-medium truncate">{notif.typsa_unit_name}</span>
                            {notif.typsa_unit_type && (
                              <span className="text-muted-foreground shrink-0">({notif.typsa_unit_type})</span>
                            )}
                          </div>
                        )}
                        
                        {/* Opportunity */}
                        {notif.opportunity_id && (
                          <div className="flex items-center gap-1 min-w-0">
                            <Target className="w-3 h-3 shrink-0" />
                            <span className="truncate">{getOpportunityName(notif.opportunity_id)}</span>
                          </div>
                        )}
                        
                        {/* Owner */}
                        {notif.owner && (
                          <div className="flex items-center gap-1 min-w-0">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{notif.owner}</span>
                          </div>
                        )}
                        
                        {/* Location for activities */}
                        {notif.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span>{notif.location}</span>
                          </div>
                        )}
                        
                        {/* Contacts (using backend enriched names) */}
                        {notif.contact_names && notif.contact_names.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span>
                              {notif.contact_names.length <= 2 
                                ? notif.contact_names.join(', ')
                                : `${notif.contact_names[0]}, ${notif.contact_names[1]} +${notif.contact_names.length - 2}`}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Transcript preview for voice notes */}
                      {notif.transcript_preview && (
                        <p className="text-xs text-muted-foreground mt-2 italic line-clamp-2 bg-muted/50 p-2 rounded">
                          "{notif.transcript_preview}"
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isAdmin && remindable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                          onClick={(e) => { e.stopPropagation(); openReminderForSingle(notif); }}
                          data-testid={`remind-single-${notif.type}-${notif.id}`}
                          title="Recordar por correo"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => handleDeleteClick(e, notif)}
                        data-testid={`delete-notification-${notif.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Helper function to render text notes content
  function renderTextNotesContent() {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en notas de texto..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9"
              data-testid="search-my-text-notes"
            />
          </div>
        </div>

        {filteredTextNotes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground text-center">
                {searchText
                  ? 'No se encontraron notas con ese criterio'
                  : 'No hay notas de texto en el sistema'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTextNotes.map((note) => (
              <Card
                key={note.id}
                className="cursor-pointer hover:shadow-md transition-shadow group relative"
                onClick={() => navigate(`/text-notes/${note.id}`)}
                data-testid={`my-text-note-${note.id}`}
              >
                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-destructive hover:text-destructive-foreground z-10"
                  onClick={(e) => handleDeleteTextNote(note.id, e)}
                  title="Eliminar nota"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <CardHeader className="pb-2 pr-10">
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={getTypeColor(note.type)}>
                      {getTypeLabel(note.type)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{note.date}</span>
                  </div>
                  <CardTitle className="text-lg font-medium tracking-tight group-hover:text-primary transition-colors">
                    {note.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                    {note.company_id && getCompanyName(note.company_id) && (
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        <span>{getCompanyName(note.company_id)}</span>
                      </div>
                    )}
                    {note.opportunity_id && getOpportunityName(note.opportunity_id) && (
                      <div className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        <span>{getOpportunityName(note.opportunity_id)}</span>
                      </div>
                    )}
                  </div>
                  {note.summary && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {note.summary}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderVoiceNotesContent() {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en mis notas de voz..."
              value={searchVoice}
              onChange={(e) => setSearchVoice(e.target.value)}
              className="pl-9"
              data-testid="search-my-voice-notes"
            />
          </div>
        </div>

        {filteredVoiceNotes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Mic className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground text-center">
                {searchVoice
                  ? 'No se encontraron notas con ese criterio'
                  : 'No tienes notas de voz'}
              </p>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Usa el botón de micrófono para grabar tu primera nota
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {groupedVoiceNotes.map((group, groupIndex) => {
              const isExpanded = expandedNoteGroups.has(String(groupIndex));
              const isToday = new Date(group.startDate).toDateString() === new Date().toDateString();
              
              return (
                <div key={group.weekKey} className="border rounded-lg overflow-hidden">
                  {/* Collapsible Week header */}
                  <div 
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                    onClick={() => toggleNoteGroup(groupIndex)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {getWeekLabel(group.startDate)}
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {group.notes.length} nota{group.notes.length !== 1 ? 's' : ''}
                    </Badge>
                    {isToday && <Badge className="bg-green-100 text-green-800 text-xs">Hoy</Badge>}
                    <div className="flex-1" />
                  </div>
                  
                  {/* Notes grid - collapsible */}
                  {isExpanded && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 pt-2">
                      {group.notes.map((note) => (
                        <Card
                          key={note.id}
                          className="cursor-pointer hover:shadow-md transition-shadow group relative"
                          onClick={() => navigate(`/voice-notes/${note.id}`)}
                          data-testid={`my-voice-note-${note.id}`}
                        >
                          {/* Delete button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-destructive hover:text-destructive-foreground z-10"
                            onClick={(e) => handleDeleteVoiceNote(note.id, e)}
                            title="Eliminar nota"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          
                          <CardHeader className="pb-2 pr-10">
                            <div className="flex items-start justify-between mb-2">
                              {getStatusBadge(note)}
                              <span className="text-xs text-muted-foreground">
                                {new Date(note.date_time).toLocaleDateString('es-ES', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </span>
                            </div>
                            <CardTitle className="text-lg font-medium tracking-tight group-hover:text-primary transition-colors">
                              Nota de voz - {new Date(note.date_time).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </CardTitle>
                          </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {note.company_id && getCompanyName(note.company_id) && (
                              <div className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                <span>{getCompanyName(note.company_id)}</span>
                              </div>
                            )}
                            {note.opportunity_id && getOpportunityName(note.opportunity_id) && (
                              <div className="flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                <span>{getOpportunityName(note.opportunity_id)}</span>
                              </div>
                            )}
                          </div>
                          {note.transcript_text && (
                            <p className="text-muted-foreground line-clamp-2 pt-1">
                              {note.transcript_text}
                            </p>
                          )}
                          {note.extracted_tasks_preview && note.extracted_tasks_preview.length > 0 && (
                            <div className="text-xs text-muted-foreground pt-1">
                              {note.extracted_tasks_preview.length} tarea(s) extraida(s)
                            </div>
                          )}
                        </div>
                      </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
};

export default MySpace;
